import { createContext, FC, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { I18NNamespace } from "../enums/I18N";
import { i18n as i18nType } from "i18next";
import { useDebouncedCallback } from "use-debounce";
import { chunkArray } from "../utils/arrayUtils";
import { getLabels } from "../api/getLabels";

enum TranslationStatus {
  NotFetched,
  FetchedNotPresent,
  Fetched,
}

interface LabelsPerNamespace {
  [key: string]: SingleNsLabels;
}

interface SingleNsLabels {
  [key: string]: TranslationStatus;
}

interface MissingLabelsContextValue {
  addMissingLabels: (ns: I18NNamespace, keys: string[]) => void;
  isLabelAlreadyAdded: (ns: I18NNamespace, key: string) => boolean;
}

const DEFAULT_CONTEXT_VALUE: MissingLabelsContextValue = {
  addMissingLabels: () => {},
  isLabelAlreadyAdded: () => {
    return true;
  },
};

interface Props {
  i18n: i18nType;
  activeNamespace: I18NNamespace;
  initLabelsConfig?: {
    initialLabels: string[];
    showContentBeforeInitialsAreFetched: boolean;
  };
}

const MissingLabelsContext = createContext<MissingLabelsContextValue>(DEFAULT_CONTEXT_VALUE);

const FETCH_LABELS_DELAY = 150;
const MAX_LABELS_PER_REQUEST = 50;

export const MissingLabelsContextProvider: FC<Props> = ({
  children,
  i18n,
  activeNamespace,
  initLabelsConfig,
}) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [areInitialLabelsFetched, setAreInitialLabelsFetched] = useState(false);
  const [labels, setLabels] = useState(
    Object.values(I18NNamespace).reduce((acc: LabelsPerNamespace, curr) => {
      acc[curr] = {};
      return acc;
    }, {})
  );

  const addMissingLabels = useCallback(
    (ns: I18NNamespace, keys: string[]) => {
      const out = { ...labels };
      let haveToUpdate = false;
      for (const key of keys) {
        if (out[ns][key] === undefined) {
          haveToUpdate = true;
          out[ns][key] = TranslationStatus.NotFetched;
        }
      }
      if (haveToUpdate) {
        setLabels(out);
      }
    },
    [labels]
  );

  const isLabelAlreadyAdded = useCallback(
    (ns: I18NNamespace, key: string) => {
      return labels[ns][key] !== undefined;
    },
    [labels]
  );

  const missingLabelsForActiveNS = useMemo(() => {
    return Object.keys(labels[activeNamespace]).reduce((acc: string[], curr) => {
      if (labels[activeNamespace][curr] === TranslationStatus.NotFetched) acc.push(curr);
      return acc;
    }, []);
  }, [activeNamespace, labels]);

  const fetchMissingLabels = useDebouncedCallback(async () => {
    if (missingLabelsForActiveNS.length > 0) {
      const language = i18n.language.toLowerCase();
      const labelsPromises = chunkArray(missingLabelsForActiveNS, MAX_LABELS_PER_REQUEST).map(
        (labelsToFetch) =>
          getLabels(
            language.toLowerCase(),
            labelsToFetch,
            activeNamespace === I18NNamespace.Mobile,
            window.location.origin
          )
      );
      const labelsResults = await Promise.all(labelsPromises);
      const fetchedLabels: { [key: string]: string | null | undefined } = labelsResults.reduce(
        (acc, labels) => ({ ...acc, ...labels }),
        {}
      );
      i18n.addResources(language, activeNamespace, fetchedLabels);
      let mutLabels = { ...labels };
      for (const missingLabel of missingLabelsForActiveNS) {
        mutLabels[activeNamespace][missingLabel] = !!fetchedLabels[missingLabel as string]
          ? TranslationStatus.Fetched
          : TranslationStatus.FetchedNotPresent;
      }
      setLabels(mutLabels);
      const localStorageKey = `i18next_res_${language}-${activeNamespace}`;
      localStorage.setItem(
        localStorageKey,
        JSON.stringify({
          ...JSON.parse(localStorage.getItem(localStorageKey) as string),
          ...fetchedLabels,
        })
      );
      i18n.reloadResources();
      i18n.changeLanguage(language);
    }
    if (!areInitialLabelsFetched) {
      setAreInitialLabelsFetched(true);
    }
  }, FETCH_LABELS_DELAY);

  const getLabelsFromStorageForNS = useCallback(
    (ns: I18NNamespace) => {
      const localStorageKey = `i18next_res_${i18n.language.toLowerCase()}-${ns}`;
      const storedLabels = JSON.parse(localStorage.getItem(localStorageKey) as string);
      const out: SingleNsLabels = {};
      for (const key of Object.keys(storedLabels)) {
        out[key] = TranslationStatus.Fetched;
      }
      return out;
    },
    [i18n]
  );

  const init = useCallback(() => {
    const out: LabelsPerNamespace = { ...labels };
    for (const ns of Object.values(I18NNamespace)) {
      out[ns] = getLabelsFromStorageForNS(ns);
    }
    if (!!initLabelsConfig) {
      for (const initLabel of initLabelsConfig.initialLabels) {
        for (const ns of Object.values(I18NNamespace)) {
          if (out[ns][initLabel] === undefined) {
            out[ns][initLabel] = TranslationStatus.NotFetched;
          }
        }
      }
    }
    setLabels(out);
    setIsInitialized(true);
    fetchMissingLabels();
  }, [getLabelsFromStorageForNS, initLabelsConfig, labels, fetchMissingLabels]);

  useEffect(() => {
    if (!isInitialized) {
      init();
    }
  }, [i18n, isInitialized, init]);

  useEffect(() => {
    if (isInitialized && areInitialLabelsFetched && missingLabelsForActiveNS.length > 0) {
      fetchMissingLabels();
    }
  }, [i18n, isInitialized, areInitialLabelsFetched, missingLabelsForActiveNS, fetchMissingLabels]);

  const showChildren = useMemo(() => {
    if (!initLabelsConfig) return true;
    return initLabelsConfig.showContentBeforeInitialsAreFetched ? true : areInitialLabelsFetched;
  }, [initLabelsConfig, areInitialLabelsFetched]);

  return (
    <MissingLabelsContext.Provider
      value={{
        addMissingLabels,
        isLabelAlreadyAdded,
      }}
    >
      {showChildren && children}
    </MissingLabelsContext.Provider>
  );
};

export const useMissingLabelsContext = () => useContext(MissingLabelsContext);
